import { useLayoutEffect, type RefObject } from "react";
import { menuPlacement } from "../lib/menuPlacement";

/* 🔴 W79 — A `⋯` MENU'S LIST LIVES IN THE TOP LAYER, NEVER INSIDE THE BOX THAT OPENED IT. ONE mechanism, used by every
 * `⋯` list that opens over a page (`AssignmentMenu`, the day cell's; `WeekMenu`, the Program board's) — two menus, one
 * way of drawing a menu, so a fix to one is a fix to both.
 *
 * His report, 2026-09-25 ~19:00: the day cell's menu opened with half its items cut off. MEASURED: the square closed
 * cell is `overflow: hidden` (so a closed cell stays square), and the list was an `absolute` child of it — clipped 21 px
 * on the left and 50–114 px at the bottom at 1440; in Safari the menu was invisible bar an empty box edge. The week
 * menu (Fable's add) was MEASURED at 375 opening at x −81…95 — off the left edge of the screen, no item reachable.
 *
 * A popover is in the top layer: no ancestor's overflow, containment or transform can clip it, and it stacks above a
 * `<dialog>` window opened before it (a portal to `body` would sit UNDER one). It has no parent to be positioned by, so
 * `menuPlacement` decides where, and it follows its `⋯` on scroll and resize. Written through the ref, not state: a
 * re-render per scroll frame would buy nothing. `@types/react` 18 has no `popover` prop, hence `setAttribute`.
 * ⚠️ A browser without the popover API (Safari < 17) still gets `position: fixed` + the placement.
 */
export function useTopLayerMenu(
  open: boolean,
  trigger: RefObject<HTMLElement>,
  list: RefObject<HTMLElement>,
): void {
  useLayoutEffect(() => {
    if (!open) return;
    const el = list.current, t = trigger.current;
    if (el === null || t === null) return;
    if (typeof el.showPopover === "function") {
      el.setAttribute("popover", "manual");
      el.showPopover();
    }
    const place = () => {
      const p = menuPlacement(t.getBoundingClientRect(), { width: el.offsetWidth, height: el.offsetHeight },
                              { width: window.innerWidth, height: window.innerHeight });
      el.style.left = `${p.left}px`;
      el.style.top = `${p.top}px`;
      el.dataset.side = p.above ? "above" : "below";
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, trigger, list]);
}
